import { backendFetch } from './backendFetch';
import { getClientSessionKey } from '../utils/sessionKey';
import type { CrawlProgress, KeywordData } from '../types/keyword.types';

export interface BrowserWorkerHint {
  enabled: boolean;
  pollIntervalSec: number;
  escalateAfterSec: number;
}

export interface EnqueueSuggestResponse {
  jobId: string;
  status: string;
  cached?: boolean;
  browserWorker?: BrowserWorkerHint;
}

export interface JobStatusResponse {
  jobId: string;
  status: 'waiting' | 'active' | 'completed' | 'failed';
  duration_seconds: number | null;
  progress: CrawlProgress | null;
  summary?: Record<string, unknown> | null;
  error?: string;
}

export interface JobKeywordsResponse {
  jobId: string;
  status: string;
  offset: number;
  limit: number;
  total: number;
  hasMore: boolean;
  keywords: KeywordData[];
}

export interface PublicKeywordsStatsResponse {
  keywords_discovered: number;
}

interface ApiEnvelope<T> {
  code: number;
  msg: string;
  data: T;
}

async function parseJson<T>(response: Response): Promise<T> {
  const body = (await response.json()) as ApiEnvelope<T>;
  if (!response.ok || body.code >= 400) {
    throw new Error(body.msg || `HTTP ${response.status}`);
  }
  return body.data;
}

export async function enqueueSuggest(params: {
  seed: string;
  language_code: string;
  location_code: string;
  force_refresh?: boolean;
}): Promise<EnqueueSuggestResponse> {
  const response = await backendFetch('/keywords/suggest', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...params,
      client_session_key: getClientSessionKey(),
    }),
  });
  return parseJson<EnqueueSuggestResponse>(response);
}

export async function fetchPublicKeywordsStats(): Promise<PublicKeywordsStatsResponse> {
  const response = await backendFetch('/keywords/stats');
  return parseJson<PublicKeywordsStatsResponse>(response);
}

export async function pollJobStatus(jobId: string): Promise<JobStatusResponse> {
  const response = await backendFetch(`/keywords/jobs/${jobId}`);
  return parseJson<JobStatusResponse>(response);
}

export async function fetchJobKeywords(
  jobId: string,
  offset: number,
  limit = 100,
): Promise<JobKeywordsResponse> {
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
  });
  const response = await backendFetch(
    `/keywords/jobs/${jobId}/keywords?${params}`,
  );
  return parseJson<JobKeywordsResponse>(response);
}

export async function browserWorkerPost<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const response = await backendFetch(
    `/browser-worker${normalizedPath}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );

  const text = await response.text();

  if (!response.ok) {
    let message = text || `HTTP ${response.status}`;
    try {
      const err = JSON.parse(text) as ApiEnvelope<null>;
      if (err.msg) message = err.msg;
    } catch {
      /* keep raw text */
    }
    throw new Error(message);
  }

  // heartbeat / keywords / complete / fail return 200 with empty body (@SkipApiResponse)
  if (!text.trim()) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}
